import { CardActionArea, makeStyles } from '@material-ui/core';
import TaskIcon from '@material-ui/icons/Assignment';
import MilestoneIcon from '@material-ui/icons/Flag';
import classNames from 'classnames';
import React from 'react';

import { ReactComponent as TimelineFAQSvg } from '../../../assets/timeline_search_faq.svg';
import { ReactComponent as TimelinePostSvg } from '../../../assets/timeline_search_post.svg';
import {
  PostType,
  PublishStatus,
  QuestionStatus,
  SearchPostBody,
  SearchQuestionBody,
} from '../../../graphql/gen/types';
import { bottomBorderStyle, colorPalette, spacings, TextSizesEnum } from '../../../theme';
import { LinesEllipsis } from '../../shared/primitives/LinesEllipsis';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { PlexName } from '../common/PlexName';
import { PostVisibility } from '../common/PostVisibility';
import { useSelectedPlexPost } from '../details/post/useSelectedPlexPost';
import { useSelectedPlexQuestion } from '../details/question/useSelectedQuestion';
import { logAnalytics } from '../shared/logAnalytics';
import { TimelineSearchEventSvg } from './TimelineSearchEventSvg';

interface SearchItemData {
  icon: React.ReactNode;
  title: string;
  body: string;
  plexName: string;
  plexColor: string | null | undefined;
  publishStatus: PublishStatus | QuestionStatus;
  publishedDate: Date;
  viewOnClick: () => void;
}

interface Props {
  searchResult: { id: string; body: SearchPostBody | SearchQuestionBody };
  plexName: string;
  plexColor: string | null | undefined;
}

export const SearchResultItem = React.memo(
  ({ searchResult, plexName, plexColor }: Props): React.ReactElement | null => {
    const classes = useStyles();
    const [, setSelectedPlexPost] = useSelectedPlexPost();
    const [, setSelectedPlexQuestion] = useSelectedPlexQuestion();
    if (!searchResult.body) {
      return null;
    }

    const getItemData = (): SearchItemData | null => {
      switch (searchResult.body?.__typename) {
        case 'SearchPostBody': {
          return {
            icon:
              searchResult.body?.postType === PostType.Event ? (
                <TimelineSearchEventSvg />
              ) : searchResult.body?.postType === PostType.Milestone ? (
                <MilestoneIcon fontSize="large" />
              ) : searchResult.body?.postType === PostType.Task ? (
                <TaskIcon fontSize="large" />
              ) : (
                <TimelinePostSvg />
              ),
            title: searchResult.body.title || '',
            body: searchResult.body.body || '',
            plexName,
            plexColor,
            // TODO this is wrong, we should always have a value here
            publishStatus: searchResult.body.publishStatus || PublishStatus.Internal,
            publishedDate: searchResult.body.updatedAt || '',
            viewOnClick: (): void => {
              logAnalytics({ name: 'search result clicked' });
              setSelectedPlexPost({ permalink: searchResult.body.permalink, commentsVisible: false });
            },
          };
        }
        case 'SearchQuestionBody': {
          return {
            icon: <TimelineFAQSvg />,
            title: searchResult.body.text || '',
            body: searchResult.body.acceptedAnswerBody || '',
            plexName,
            plexColor,
            publishStatus: searchResult.body.questionStatus || QuestionStatus.Internal,
            publishedDate: searchResult.body.updatedAt || '',
            viewOnClick: (): void => {
              logAnalytics({ name: 'search result clicked' });
              setSelectedPlexQuestion({ permalink: searchResult.body.permalink, commentsVisible: false });
            },
          };
        }
        default: {
          return null;
        }
      }
    };

    const itemData = getItemData();
    if (!itemData) {
      return null;
    }

    return (
      <CardActionArea className={classes.eventRoot} onClick={itemData.viewOnClick}>
        <div className={classNames(classes.columnType)}>{itemData.icon}</div>
        <div className={classNames(classes.columnDescription)}>
          <PlexTypography className={classes.eventTitle} text={itemData.title} size={TextSizesEnum.Normal} />
          <PlexTypography className={classes.eventText} size={TextSizesEnum.XSmall}>
            <LinesEllipsis text={itemData.body} maxLine={2} />
          </PlexTypography>
        </div>
        <div className={classNames(classes.columnPlex)}>
          <PlexName name={itemData.plexName} color={itemData.plexColor} className={classes.eventPlexName} />
        </div>
        <div className={classNames(classes.columnVisibility, classes.eventPublishStatus)}>
          <PostVisibility plexPublishStatus={itemData.publishStatus} plexName={itemData.plexName} />
        </div>
        <div className={classNames(classes.columnDate)}>
          <PlexTypography
            className={classNames(classes.eventDate)}
            text={new Date(itemData.publishedDate).toLocaleTimeString()}
            size={TextSizesEnum.Normal}
          />
          <PlexTypography
            className={classNames(classes.eventDate)}
            text={new Date(itemData.publishedDate).toDateString()}
            size={TextSizesEnum.Normal}
          />
        </div>
      </CardActionArea>
    );
  }
);

const useStyles = makeStyles((theme) => ({
  columnType: {
    display: 'flex',
    width: 60,
    marginLeft: 10,
  },
  columnDescription: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: 0,
    borderRadius: 0,
    justifyContent: 'flex-start',
  },
  columnPlex: {
    display: 'flex',
    width: 160,
    marginRight: 16,
  },
  columnVisibility: {
    display: 'flex',
    width: 60,
  },
  columnDate: {
    display: 'flex',
    width: 160,
    flexDirection: 'column',
  },
  eventRoot: {
    display: 'flex',
    flexDirection: 'row',
    padding: 14,
    ...bottomBorderStyle,
  },
  eventTitle: {
    color: colorPalette.primaryColorBlue._500,
  },
  eventText: {
    color: colorPalette.purpleGray._500,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 500,
    marginTop: spacings.small,
  },
  eventPlexName: {
    maxWidth: 160,
    height: 'auto',
  },
  eventPublishStatus: {
    display: 'flex',
    color: colorPalette.purpleGray._500,
    fontSize: theme.typography.fontSize,
  },
  eventDate: {
    color: colorPalette.purpleGray._400,
  },
}));
